import { AfterViewInit, Component, computed, effect, OnInit, signal } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { AppService } from 'src/app/infrastructure/services/app-services/app.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { DomainProfileService } from 'src/app/infrastructure/services/domain-profile-services/domain-profile.service';
import { FooterService } from 'src/app/features/shared/footer/footer.service';
import { HeaderService } from 'src/app/features/shared/header/services/header.service';
import { IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { ImageBoxComponent } from 'src/app/features/shared/image-boxes/image-box/image-box.component';
import { InputControlComponent } from 'src/app/features/shared/controls/container/input-control/input-control.component';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { _CentralPageService } from 'src/app/features/shared/central-page/services/_central-page.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { loadKey } from 'src/app/infrastructure/services/loading-services/models/loadKey';
import { passwordMatchValidator, passwordValidators } from '../../../functions/password.validators';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';

// request: e-mailadres invullen → requestSent/requestFailed. Via de resetlink uit de mail
// (?email=…&code=…): reset → resetDone/resetFailed.
type TRecoverViewState = 'request' | 'requestSent' | 'requestFailed' | 'reset' | 'resetDone' | 'resetFailed';

@Component({
    selector: 'app-recover-details',
    templateUrl: './recover-details.component.html',
    standalone: true,
    imports: [
        SharedModule,
        InputControlComponent,
        ButtonActionComponent,
        ImageBoxComponent,
    ],
    styleUrls: [
        '../../../account.scss',
        './recover-details.component.scss',
    ]
})
export class RecoverDetailsComponent
    extends _SectionBaseComponent
    implements OnInit, AfterViewInit
{
    private readonly __activatedRouteService = injectRequired(ActivatedRouteService);
    private readonly __appProfileService = injectRequired(DomainProfileService);
    private readonly __appService = injectRequired(AppService);
    private readonly __authService = injectRequired(AuthService);
    private readonly __formBuilder = injectRequired(UntypedFormBuilder);

    protected override readonly _centralPageService = injectRequired(_CentralPageService);
    protected override readonly _footerService = injectRequired(FooterService);
    protected override readonly _headerService = injectRequired(HeaderService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _pageService = injectRequired(_PageService);

    override out_onEscape = this._out_onEscape;
    override out_onPageLoaded = this._out_onPageLoaded;

    override readonly sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    override template_footerLeft$$ = this._template_footerLeft$$;
    override template_sectionDetails_extraButtons$$ = this._template_sectionDetails_extraButtons$$;
    override template_sectionEdit_extraButtons$$ = this._template_sectionEdit_extraButtons$$;

    // Zelfde header/footer als RegisterDetailsComponent (auth-pagina met domainlogo).
    override footerData: Partial<IFooterData> = {
        showInfoBlock: true,
        hideSideBlockLeft: true,
        hideSideBlockRight: true,
    };
    override headerData: Partial<IHeaderData> = {
        sideBlockLeft_hide: false,
        sideBlockRight_hide: true,
        title: this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '',
        subTitle: this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '',
        subTitle_hide: false,
    };

    private readonly __viewState$$ = signal<TRecoverViewState>('request');

    private __email: string | null = null;
    private __code: string | null = null;

    private __requestForm: UntypedFormGroup = this.__formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
    });
    private __resetForm: UntypedFormGroup = this.__formBuilder.group({
        password: ['', passwordValidators],
        passwordRepeat: ['', Validators.required],
    }, { validators: passwordMatchValidator('password', 'passwordRepeat') });

    get RequestForm() { return this.__requestForm; }
    // Cast naar FormControl: UntypedFormGroup.controls levert AbstractControl, maar
    // app-input-control verwacht een FormControl-input (runtime zijn het FormControls).
    get RequestFormControls() { return this.__requestForm.controls as { [key: string]: FormControl }; }
    get ResetForm() { return this.__resetForm; }
    get ResetFormControls() { return this.__resetForm.controls as { [key: string]: FormControl }; }

    logoPath$$ = computed(() => this.__appProfileService.logo_url$$());
    viewState$$ = this.__viewState$$.asReadonly();

    loadKey = loadKey.loadingPage;
    showErrors = false;

    constructor() {
        super();

        // Domain-0-fallback (titel/subtitel) kan async binnenkomen ná de eerste render —
        // reactief bijwerken, zelfde patroon als RegisterDetailsComponent.
        effect(() => {
            const __title = this.__appProfileService.title$$() ?? this.__appService.title$$() ?? '';
            const __subTitle = this.__appProfileService.title_sub$$() ?? this.__appService.sutTitle$$() ?? '';
            this._headerService.headerData_set({ title: __title, subTitle: __subTitle });
        });
    }

    override ngOnInit(): void {
        super._ngOnInit();

        this.__email = this.__activatedRouteService.queryParams?.['email'] ?? null;
        this.__code = this.__activatedRouteService.queryParams?.['code'] ?? null;
        this.__viewState$$.set(this.__email && this.__code ? 'reset' : 'request');

        this._footerService.info_set(this.__appService?.version$$() ?? null);
        this._footerService.infoTooltip_set(this.__appService?.versionDetail$$() ?? null);
        this.emit_pageLoaded();
    }

    override emit_onEscape(): void {
        super._emit_onEscape();
    }

    override emit_pageLoaded(): void {
        super._emit_pageLoaded();
    }

    emit_gotoLogin(): void {
        this._pageService.page_goto(routePage.login);
    }

    // Terug naar het aanvraagformulier (na een mislukte aanvraag of een ongeldige resetlink).
    emit_requestAgain(): void {
        this.__email = null;
        this.__code = null;
        this.showErrors = false;
        this.__viewState$$.set('request');
    }

    emit_requestReset(): void {
        if (this.__requestForm.invalid) {
            this.showErrors = true;
            return;
        }

        // De backend zelf is al enumeratie-veilig (forgot-password geeft altijd 200, ongeacht of
        // het account bestaat) — een echte HTTP-fout (netwerk/5xx) hoeft dus niet ook nog eens
        // als "verstuurd" vermomd te worden; dat maakte een kapotte request onzichtbaar.
        this.__authService.postForgotPassword$(this.__requestForm.value.email)
            .subscribe({
                next: () => this.__viewState$$.set('requestSent'),
                error: () => this.__viewState$$.set('requestFailed'),
            });
    }

    emit_resetPassword(): void {
        if (this.__resetForm.invalid || !this.__email || !this.__code) {
            this.showErrors = true;
            return;
        }

        this.__authService.postResetPassword$(this.__email, this.__code, this.__resetForm.value.password)
            .subscribe({
                next: () => this.__viewState$$.set('resetDone'),
                error: () => this.__viewState$$.set('resetFailed'),
            });
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }
}
